/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Fragment } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';

const highlightStyles = cssProps`${{
  fontWeight: 700
}}`;

type Props = {
  /**
   * The text string to apply highlighting to.
   */
  children?: string;
  /**
   * The text string to match.
   */
  matchText?: string;
};

/**
 * Renders a text string with highlighting applied to the given text to match.
 */
export function HighlightText({ children = '', matchText = '' }: Props) {
  return highlighter(children, matchText);
}

const HIGHLIGHT_TOKEN = '__@@__';
const HIGHLIGHT_PATTERN = `${HIGHLIGHT_TOKEN}$&${HIGHLIGHT_TOKEN}`;

function highlighter(unhighlightedText: string, matchText: string) {
  if (matchText.length > 0 && unhighlightedText.length > 0) {
    const escapedMatchText = escapeRegExp(matchText);
    const highlightedText = unhighlightedText.replace(
      new RegExp(escapedMatchText, 'gi'),
      HIGHLIGHT_PATTERN
    );
    const tokens = highlightedText.split(HIGHLIGHT_TOKEN);
    const nodes = tokens.map((current, index) =>
      index % 2 == 0 ? current : <span class={highlightStyles}>{current}</span>
    );
    return <Fragment>{nodes}</Fragment>;
  }
  return <Fragment>{unhighlightedText}</Fragment>;
}

function escapeRegExp(str: string) {
  // copied from:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
  return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
